import { Api } from "../../api/Api";
import moment from "moment";

const state = {
  recordedDates: [],
  categories: [],
  recordedTransactions: [],
  allTransactions: [],
  allTransactionsTotal: 0,
  allTransactionsLoading: false
};

const mutations = {
  UPDATE_RECORDED_DATES(state, payload) {
    state.recordedDates = payload;
  },
  UPDATE_CATEGORIES(state, payload) {
    state.categories = payload;
  },
  UPDATE_RECORDED_TRANSACTIONS(state, payload) {
    state.recordedTransactions = payload;
  },
  UPDATE_ALL_TRANSACTIONS(state, payload) {
    state.allTransactions = payload;
  },
  UPDATE_ALL_TRANSACTIONS_TOTAL(state, payload) {
    state.allTransactionsTotal = payload;
  },
  SET_ALL_TRANSACTIONS_LOADING(state, payload) {
    state.allTransactionsLoading = payload;
  }
};

const actions = {
  async loadRecordedDates({ commit, dispatch }) {
    try {
      const response = await Api().get(
        "/cards-management/transactions/recorded/dates"
      );
      const dates = (response.data || []).map(record =>
        moment(record.salesDate).format("YYYY-MM-DD")
      );
      commit("UPDATE_RECORDED_DATES", dates);
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message: "Unable to load recorded card sales dates",
          status: "red"
        },
        { root: true }
      );
    }
  },

  async loadCardCategories({ commit, dispatch }) {
    try {
      const response = await Api().get("/cards-management/categories");
      commit("UPDATE_CATEGORIES", response.data || []);
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message: "Unable to load card categories",
          status: "red"
        },
        { root: true }
      );
    }
  },

  async loadTransactionsForDate({ commit, dispatch }, salesDate) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const response = await Api().get(
        `/cards-management/transactions/by-date/${salesDate}`
      );
      commit("UPDATE_RECORDED_TRANSACTIONS", response.data || []);
      return response.data || [];
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message: "Unable to load card sales for selected date",
          status: "red"
        },
        { root: true }
      );
      return [];
    } finally {
      dispatch("isPageLoading", false, { root: true });
    }
  },

  async loadAllTransactions(
    { commit, dispatch },
    { page = 1, limit = 50, salesDate, cardCategoryId } = {}
  ) {
    try {
      commit("SET_ALL_TRANSACTIONS_LOADING", true);
      const params = { page, limit };
      if (salesDate) params.salesDate = salesDate;
      if (cardCategoryId) params.cardCategoryId = cardCategoryId;
      const response = await Api().get("/cards-management/transactions", {
        params
      });
      commit("UPDATE_ALL_TRANSACTIONS", response.data?.data || []);
      commit("UPDATE_ALL_TRANSACTIONS_TOTAL", response.data?.total || 0);
      return response.data;
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message: "Unable to load card transactions",
          status: "red"
        },
        { root: true }
      );
      return null;
    } finally {
      commit("SET_ALL_TRANSACTIONS_LOADING", false);
    }
  },

  async recordCardTransactions({ dispatch }, payload) {
    try {
      dispatch("isLoading", true, { root: true });
      const response = await Api().post(
        "/cards-management/transactions",
        payload
      );
      const approvalRequired = response.data?.approvalRequired;
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message: approvalRequired
            ? "Card transactions recorded and pending approval"
            : "Card transactions recorded successfully",
          status: "success"
        },
        { root: true }
      );
      return { success: true, approvalRequired };
    } catch (e) {
      const message = Array.isArray(e?.response?.data?.message)
        ? e.response.data.message.join(", ")
        : e?.response?.data?.message || "Unable to record card transactions";
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message,
          status: "red"
        },
        { root: true }
      );
      return { success: false };
    } finally {
      dispatch("isLoading", false, { root: true });
    }
  }
};

const getters = {
  getRecordedDates: state => state.recordedDates,
  getCategories: state => state.categories,
  getRecordedTransactions: state => state.recordedTransactions,
  getAllTransactions: state => state.allTransactions,
  getAllTransactionsTotal: state => state.allTransactionsTotal,
  getAllTransactionsLoading: state => state.allTransactionsLoading
};

export const cards = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
